import {
  FieldLabel,
  MasterDataSelect,
  NumericMasterDataSelect,
  TextField,
} from "@/components/copy-operations/field-inputs";
import { DataTable } from "@/components/shared/data-table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import type { Contract, ContractData } from "@/lib/coach-manager/types";

interface EditableContractHeaderProps {
  contract: Contract;
  targetBuId: string;
  onChange: (contract: Contract) => void;
  disabled?: boolean;
}

function EditableContractDataRow({
  row,
  targetBuId,
  onChange,
  disabled,
}: {
  row: ContractData;
  targetBuId: string;
  onChange: (row: ContractData) => void;
  disabled?: boolean;
}) {
  return (
    <tr className="border-b last:border-b-0">
      <td className="px-4 py-2">
        <NumericMasterDataSelect
          targetBuId={targetBuId}
          entityType="contract_data_title"
          value={row.ContractDataTitleSerialNo}
          onChange={(v) => onChange({ ...row, ContractDataTitleSerialNo: v })}
          disabled={disabled}
          className="w-40"
        />
      </td>
      <td className="px-4 py-2">
        <Input
          className="h-8 w-32 text-sm"
          value={row.TextValue ?? ""}
          onChange={(e) => onChange({ ...row, TextValue: e.target.value })}
          disabled={disabled}
        />
      </td>
      <td className="px-4 py-2">
        <Input
          type="number"
          className="h-8 w-24 text-sm"
          value={row.NumberValue}
          onChange={(e) => onChange({ ...row, NumberValue: Number(e.target.value) || 0 })}
          disabled={disabled}
        />
      </td>
      <td className="px-4 py-2">
        <Input
          type="number"
          className="h-8 w-24 text-sm"
          value={row.MoneyValue}
          onChange={(e) => onChange({ ...row, MoneyValue: Number(e.target.value) || 0 })}
          disabled={disabled}
        />
      </td>
      <td className="px-4 py-2">
        <Input
          className="h-8 w-32 text-sm"
          value={row.DateValue ?? ""}
          onChange={(e) => onChange({ ...row, DateValue: e.target.value })}
          disabled={disabled}
        />
      </td>
      <td className="px-4 py-2">
        <Badge variant={row.YesNoValue ? "success" : "outline"}>
          {row.YesNoValue ? "Yes" : "No"}
        </Badge>
      </td>
    </tr>
  );
}

/**
 * The Contract's own (non-movement) fields + its `ContractData` rows,
 * editable. `CompanyID`/`DateCreated`/`Cancelled` stay read-only — CompanyID
 * is fixed to the target BU on transform, and the other two are descriptive
 * metadata Coach Manager doesn't expect edited on insert.
 */
export function EditableContractHeader({
  contract,
  targetBuId,
  onChange,
  disabled,
}: EditableContractHeaderProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
        <TextField
          label="Client ID"
          value={contract.ClientID}
          onChange={(v) => onChange({ ...contract, ClientID: v })}
          mapped
          disabled={disabled}
        />
        <MasterDataSelect
          label="Booking Type"
          targetBuId={targetBuId}
          entityType="booking_type"
          value={contract.BookingType}
          onChange={(v) => onChange({ ...contract, BookingType: v })}
          disabled={disabled}
        />
        <div className="space-y-1">
          <FieldLabel label="Company ID" mapped />
          <p className="text-sm font-medium">{contract.CompanyID ?? "—"}</p>
        </div>
        <NumericMasterDataSelect
          label="Base Serial No"
          required
          targetBuId={targetBuId}
          entityType="base"
          value={contract.BaseSerialNo}
          onChange={(v) => onChange({ ...contract, BaseSerialNo: v })}
          disabled={disabled}
        />
        <MasterDataSelect
          label="Operator ID"
          targetBuId={targetBuId}
          entityType="operator"
          value={contract.OperatorID}
          onChange={(v) => onChange({ ...contract, OperatorID: v })}
          disabled={disabled}
        />
        <div className="space-y-1">
          <FieldLabel label="Date Created" />
          <p className="text-sm font-medium">{contract.DateCreated ?? "—"}</p>
        </div>
      </div>

      {contract.ContractData && contract.ContractData.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-display text-sm font-semibold">
            Contract Data ({contract.ContractData.length})
          </h4>
          <DataTable
            headers={[
              { label: <FieldLabel label="Title #" required mapped /> },
              { label: "Text" },
              { label: <FieldLabel label="Number" required /> },
              { label: <FieldLabel label="Money" required /> },
              { label: "Date" },
              { label: <FieldLabel label="Yes/No" required /> },
            ]}
          >
            {contract.ContractData.map((row, index) => (
              <EditableContractDataRow
                key={index}
                row={row}
                targetBuId={targetBuId}
                disabled={disabled}
                onChange={(next) => {
                  const rows = [...(contract.ContractData ?? [])];
                  rows[index] = next;
                  onChange({ ...contract, ContractData: rows });
                }}
              />
            ))}
          </DataTable>
        </div>
      )}
    </div>
  );
}
